'use client';

import { createContext, useCallback, useState } from 'react';
import { Toast, type ToastType } from '@/_components';
import styles from './ToastProvider.module.css';
import type {
  ToastProviderProps,
  ToastItem,
  ToastOptions,
} from './ToastProvider.types';
import * as Sentry from '@sentry/nextjs';
import { useRouteChanges } from '@/_hooks/useRouteChanges';
import { DEFFAULT_ERROR_MESSAGE } from '@/_constants/error-messages';

const DEFAULT_TRANSIENT_DELAY = 4000 as const;

const ToastContext = createContext<ToastProviderProps>({
  addToast: (
    _type: ToastType,
    _message?: string | null,
    _options?: ToastOptions,
  ) => null,
  resetToasts: () => null,
});

let timer: ReturnType<typeof setTimeout>;

const ToastProvider: any = ({ children }: any) => {
  const [toasts, setToasts] = useState<Array<ToastItem>>([]);

  const resetToasts = useCallback(() => setToasts([]), []);

  const onAddToast = useCallback(
    (
      type: ToastType,
      message?: string | null,
      options: ToastOptions = {},
    ): any => {
      clearTimeout(timer);

      const wait: number = Math.max(
        !!message ? message?.length * 50 : DEFAULT_TRANSIENT_DELAY,
        DEFAULT_TRANSIENT_DELAY,
      );

      if (!options?.doesNotAutoClose) {
        timer = setTimeout(() => {
          resetToasts();
          options?.onClose?.();
        }, wait);
      }

      message = message || DEFFAULT_ERROR_MESSAGE[type];
      const onClose = options?.onClose;
      const newToasts: Array<ToastItem> = [{ message, onClose, type }];

      // Report the error
      if (options?.error) {
        const errorObject: object = options?.error || {};

        Sentry.captureException?.(errorObject);

        if (process.env.NODE_ENV !== 'production' && type === 'error') {
          console.error(errorObject);
        }
      }

      // Show this single toast
      return setToasts(newToasts);
    },
    [resetToasts],
  );

  // Reset state on any route change
  useRouteChanges(resetToasts);

  const onClose = useCallback(
    (toast: ToastItem) => {
      resetToasts();
      toast.onClose?.();
    },
    [resetToasts],
  );

  return (
    <ToastContext
      value={{
        addToast: onAddToast,
        resetToasts,
      }}
    >
      {toasts?.length ? (
        <div className={styles.toastProvider}>
          {toasts?.map((toast, key) => {
            return (
              <Toast key={key} {...toast} onClose={() => onClose(toast)} />
            );
          })}
        </div>
      ) : null}
      {children}
    </ToastContext>
  );
};

export { ToastProvider, ToastContext };
